import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type RootState, type BundleTypeKeys, BundleType } from '@/store';
import { BundleDownloadStatusTypeKeys } from '@/api/bundle/bundleDownloadStatus';
import { BundleGetDownloadAzureData } from '@/api/bundle/bundleGetDownload';

interface BundleDownloadState {
  bundleDownloadStatus: BundleDownloadStatusTypeKeys | null;
  bundleDownloadAzureData: BundleGetDownloadAzureData | null;
  indexDownloadAzureData: BundleGetDownloadAzureData | null;
}

const initialState: BundleDownloadState = {
  bundleDownloadStatus: null,
  bundleDownloadAzureData: null,
  indexDownloadAzureData: null
};

const bundleDownloadSlice = createSlice({
  name: 'bundleDownload',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState
  } as Record<BundleTypeKeys, BundleDownloadState>,
  reducers: {
    setBundleDownloadStatus: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleDownloadStatusTypeKeys;
      }>
    ) => {
      const { key, data } = action.payload;
      state[key].bundleDownloadStatus = data;
    },
    setBundleDownloadAzureData: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleGetDownloadAzureData | null;
      }>
    ) => {
      const { key, data } = action.payload;
      state[key].bundleDownloadAzureData = data;
    },
    setIndexDownloadAzureData: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleGetDownloadAzureData | null;
      }>
    ) => {
      const { key, data } = action.payload;
      state[key].indexDownloadAzureData = data;
    }
  }
});

export const { setBundleDownloadStatus, setBundleDownloadAzureData, setIndexDownloadAzureData } =
  bundleDownloadSlice.actions;

// RTK SELECTORS WITHOUT CALLBACK

export const selectBundleDownloadStatus = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleDownload[activeBundleType].bundleDownloadStatus
    : initialState.bundleDownloadStatus;
};

export const selectBundleDownloadAzureData = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleDownload[activeBundleType].bundleDownloadAzureData
    : initialState.bundleDownloadAzureData;
};

export const selectIndexDownloadAzureData = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleDownload[activeBundleType].indexDownloadAzureData
    : initialState.indexDownloadAzureData;
};

// CUSTOM SELECTORS WITHOUT CALLBACK

/**
 * with key
 * const bundleDownloadStatus = useAppSelector(selectBundleDownloadStatusByStoreKey(BundleType.LIGHT));
 * without key
 * const bundleDownloadStatusDefault = useAppSelector(selectBundleDownloadStatusByStoreKey());
 */
export const selectBundleDownloadStatusByStoreKey = (bundleKey?: BundleTypeKeys) => (state: RootState) => {
  const resolvedBundleKey = bundleKey ?? state.sharedData.activeBundleType;
  return resolvedBundleKey
    ? state.bundleDownload[resolvedBundleKey].bundleDownloadStatus
    : initialState.bundleDownloadStatus;
};

export const selectBundleDownloadAzureDataByStoreKey = (bundleKey?: BundleTypeKeys) => (state: RootState) => {
  const resolvedBundleKey = bundleKey ?? state.sharedData.activeBundleType;
  return resolvedBundleKey
    ? state.bundleDownload[resolvedBundleKey].bundleDownloadAzureData
    : initialState.bundleDownloadAzureData;
};

export const selectIndexDownloadAzureDataByStoreKey = (bundleKey?: BundleTypeKeys) => (state: RootState) => {
  const resolvedBundleKey = bundleKey ?? state.sharedData.activeBundleType;
  return resolvedBundleKey
    ? state.bundleDownload[resolvedBundleKey].indexDownloadAzureData
    : initialState.indexDownloadAzureData;
};

export const bundleDownloadReducer = bundleDownloadSlice.reducer;
